import {
  Button,
  Card,
  CardBody,
  CardHeader,
  type ColumnDef,
  DataTable,
  FieldEditor,
  InlineMessage,
  TextLink,
} from '@olytic/foundation';
import { CalendarSync, SquarePen } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { formatCurrency } from '../objects/format';
import { recordPath } from '../objects/routes';
import { type BuilderLine, fetchBuilder, generateSchedules } from '../quotes/quotesApi';
import type { OpportunitySummary } from './opportunityPageApi';
import { type LineRow, quoteLineRows } from './pageModel';

const messageOf = (error: unknown, fallback: string) => (error instanceof Error && error.message ? error.message : fallback);

/** The Next Step card: one line, double-click to edit. */
export function NextStepCard({ value, onSave }: { value: string | null; onSave?: ((value: string | null) => Promise<void>) | undefined }): JSX.Element {
  return (
    <Card>
      <CardHeader title="Next Step" headingLevel={3} />
      <CardBody>
        {onSave ? (
          <FieldEditor
            editor={{ kind: 'text', maxLength: 500 }}
            label="Next Step"
            value={value ?? ''}
            display={value ?? ''}
            mode="record"
            pencil={false}
            onSave={onSave}
          />
        ) : (
          <span>{value ?? '—'}</span>
        )}
      </CardBody>
    </Card>
  );
}

const columns: ColumnDef<LineRow>[] = [
  { key: 'name', label: 'Product', sortable: false },
  { key: 'total', label: 'Total Price', align: 'right', figures: true, nowrap: true, sortable: false, render: (r) => (r.total === null ? '' : formatCurrency(r.total)) },
];

/**
 * The primary quote's lines in the rail, with its Configure (the quote's own
 * page) and Regenerate Schedules actions.
 */
export function QuoteLinesCard({
  summary,
  onNavigate,
  onRegenerated,
}: {
  summary: OpportunitySummary;
  onNavigate: (href: string) => void;
  onRegenerated: () => void;
}): JSX.Element | null {
  const quote = summary.quote;
  const [lines, setLines] = useState<BuilderLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!quote) return;
    let live = true;
    setError(null);
    fetchBuilder(authenticatedFetch, quote.id)
      .then((b) => { if (live) setLines(b.lines); })
      .catch((e: unknown) => { if (live) setError(messageOf(e, 'The quote lines could not be loaded.')); });
    return () => { live = false; };
  }, [quote]);

  const rows = useMemo(() => (lines ? quoteLineRows(lines, summary, open) : []), [lines, summary, open]);

  if (!quote) {
    return (
      <Card>
        <CardHeader title="Quote Lines" headingLevel={3} />
        <CardBody>No primary quote yet.</CardBody>
      </Card>
    );
  }
  const href = recordPath('quotes', quote.id);

  async function regenerate() {
    if (!quote) return;
    setBusy(true);
    setError(null);
    try {
      const builder = await generateSchedules(authenticatedFetch, quote.id);
      setLines(builder.lines);
      onRegenerated();
    } catch (e) {
      setError(messageOf(e, 'The schedules could not be regenerated.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        headingLevel={3}
        title={
          <TextLink href={href} onClick={(e) => { if (!e.metaKey && !e.ctrlKey && !e.shiftKey) { e.preventDefault(); onNavigate(href); } }}>
            {quote.quote_number ?? quote.name ?? 'Primary Quote'}
          </TextLink>
        }
        actions={
          <>
            <Button size="icon-sm" variant="subtle" aria-label="Configure Quote" title="Configure Quote" onClick={() => onNavigate(href)}>
              <SquarePen aria-hidden className="size-4" />
            </Button>
            <Button size="icon-sm" variant="subtle" aria-label="Regenerate Schedules" title="Regenerate Schedules" loading={busy} onClick={() => void regenerate()}>
              <CalendarSync aria-hidden className="size-4" />
            </Button>
          </>
        }
      />
      {error ? <InlineMessage tone="error" role="alert" className="mx-4 mb-3">{error}</InlineMessage> : null}
      <DataTable<LineRow>
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        treeColumn="name"
        rowDepth={(r) => r.depth}
        rowExpansion={(r) =>
          r.bundle
            ? {
                expanded: open.has(r.id),
                onToggle: () => setOpen((s) => {
                  const next = new Set(s);
                  if (next.has(r.id)) next.delete(r.id);
                  else next.add(r.id);
                  return next;
                }),
              }
            : null}
        highlightedId="total"
        density="compact"
        loading={lines === null && !error}
        responsive={false}
      />
    </Card>
  );
}
